import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useIntakeEvent } from "../../lib/queries/intakeEvents";

import type { AssistTarget } from "./assist-target";
import type { guideContext } from "./guide-context";

type Context = ReturnType<typeof guideContext>;
const button =
  "min-h-11 rounded border border-slate-300 px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-brand-500";
/** Guidance only; the receipt is read under current permissions, never inferred from a saved project. */
export function VoluntaryEmailGuide({
  context,
  scope,
  onShowStep,
  onContextValidation,
}: {
  context: Context;
  scope: string | null;
  onShowStep?: (to: string, target: AssistTarget, context: Context) => void;
  onContextValidation?: (valid: boolean) => void;
}) {
  const { t } = useTranslation();
  const receipt = useIntakeEvent(scope && !context.emailUpload ? context.receiptId : undefined);
  const valid =
    !!scope &&
    (context.emailUpload ||
      receipt.data?.payload?.kind === "voluntary_email" ||
      receipt.data?.payload?.kind === "provider_email");
  useEffect(() => {
    if (valid) onContextValidation?.(true);
    else if (!scope || receipt.isError || receipt.data) onContextValidation?.(false);
  }, [valid, scope, receipt.isError, receipt.data, onContextValidation]);
  const to = context.emailUpload ? "/intake-events/email" : `/intake-events/${context.receiptId}`;
  const targets: AssistTarget[] = context.emailUpload
    ? ["email-file"]
    : ["email-preview", "email-evidence"];
  if (!context.emailUpload && receipt.data?.status === "pending_resolution")
    targets.push("email-context");
  return (
    <section aria-label={t("guide.title")} className="min-w-0 space-y-4">
      <h3 className="text-lg font-semibold">{t("voluntary_email.guide_title")}</h3>
      <p>{t(context.emailUpload ? "voluntary_email.intro" : "voluntary_email.warning")}</p>
      {receipt.data?.payload?.kind === "provider_email" && (
        <p>{t("voluntary_email.provider_help")}</p>
      )}
      {!scope && <p role="status">{t("guide.choose_firm")}</p>}
      {scope && !context.emailUpload && receipt.isPending && (
        <p role="status">{t("voluntary_email.loading")}</p>
      )}
      {scope && !context.emailUpload && (receipt.isError || (receipt.data && !valid)) && (
        <p role="alert">{t("guide.unavailable")}</p>
      )}
      <ol className="list-decimal space-y-3 pl-5">
        {(context.emailUpload
          ? ["file_help", "retry_help", "guide_after"]
          : ["guide_preview", "guide_evidence", "guide_context"]
        ).map((key) => (
          <li key={key}>{t(`voluntary_email.${key}`)}</li>
        ))}
      </ol>
      {valid && (
        <div className="flex flex-col gap-2">
          {targets.map((target) =>
            onShowStep ? (
              <button
                type="button"
                key={target}
                className={button}
                onClick={() => onShowStep(to, target, context)}
              >
                {t(`voluntary_email.targets.${target}`)}
              </button>
            ) : (
              <Link key={target} className={button} to={to}>
                {t(`voluntary_email.targets.${target}`)}
              </Link>
            ),
          )}
        </div>
      )}
      <p className="text-xs text-slate-500">{t("guide.no_effect")}</p>
    </section>
  );
}
